<template>
  <div class="people-page">
    <div class="search-input">
      <div class="page-title">People Search</div>

      <div class="content-wrapper">
        <!-- Search Bar for table -->
        <div class="search-bar">
          <input
            ref="searchQuery"
            v-model="searchQuery"
            type="text"
            placeholder="Search for person (dbl-click: clear)"
            class="search-input"
            @dblclick="clearSearch()"
          />
          <button
            :title="
              showAll
                ? 'Showing everyone — click for active tenants only'
                : 'Active tenants only — click to show everyone'
            "
            @click="showAll = !showAll"
            :class="{
              'active-class': !showAll,
              'clear-button': true,
            }"
          >
            <i
              class="fas"
              :class="showAll ? 'fa-eye' : 'fa-eye-slash'"
            ></i>
          </button>

          <button
            :title="'clear search'"
            @click="clearSearch"
            class="clear-button"
            style="margin-left: 0px"
          >
            X
          </button>
          <!-- Row Count -->
          <label>Count: </label
          ><span class="count"> {{ formatNumber(rowCount, 0) }}</span>
          <button
            :title="'add a new contact'"
            style="margin-left: 15px"
            @click="
              selectedPerson = '';
              showTenantSearchDialog = true;
            "
          >
            Add New <i class="fas fa-plus"></i>
          </button>
          <span class="hint"> (or double-click a row below to edit)</span>
          <button
            v-if="showMergeButton"
            style="margin-left: 15px"
            @click="mergePeople"
          >
            Merge <i class="fas fa-code-merge"></i>
          </button>
          <button
            title="search payments for this person"
            v-if="showSearchPaymentsButton"
            style="margin-left: 15px"
            @click="searchPayments"
          >
            Search <i class="fas fa-search-dollar"></i>
          </button>
          <span
            v-if="
              selectedPeople.length === 1 && selectedPeople[0].quickSignInCode
            "
            class="quick-code-display"
            title="Click to select, then Ctrl+C"
            @click="selectQuickCode"
          >
            {{ selectedPeople[0].quickSignInCode }}
          </span>
          <button
            :title="'Refresh list'"
            style="position: absolute; right: 34px"
            @click="fetchPeople()"
          >
            <i class="fas fa-refresh"></i>
          </button>
        </div>
        <div class="active-note">
          Note: a person is automatically set to 'active' if they are assigned
          to a current lease. If a person was a tenant on a prior lease, you'll
          see multiple rows.
        </div>
        <div class="table-container">
          <!-- Single table + sticky header (one scroll surface) -->
          <div class="table-scroll">
            <table class="people-table">
              <colgroup>
                <col
                  v-for="(header, index) in peopleTableHeaders"
                  :key="'col-' + index"
                  :style="{ width: header.width }"
                />
              </colgroup>
              <thead>
                <tr>
                  <th
                    v-for="(header, index) in peopleTableHeaders"
                    :key="'th-' + index"
                    :title="headerTitle(header)"
                    :class="{
                      'col-compact': header.compact,
                      'sorted-col': sortField === header.field,
                      'sorted-asc':
                        sortField === header.field && sortOrder === 'asc',
                      'sorted-desc':
                        sortField === header.field && sortOrder === 'desc',
                    }"
                    :style="{ textAlign: header.align || 'left' }"
                    @click="sortBy(header.field)"
                  >
                    <span class="th-label">
                      <template v-if="header.display === 'note'">N</template>
                      <template v-else-if="header.display === 'blacklist'">
                        <i class="fas fa-ban" aria-hidden="true"></i>
                      </template>
                      <template v-else-if="header.display === 'chatbot-active'">
                        <i class="fas fa-mobile-alt" aria-hidden="true"></i>
                      </template>
                      <template v-else-if="header.display === 'logged-out'">
                        <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
                      </template>
                      <template v-else>
                        {{ header.label }}
                      </template>
                    </span>
                    <i
                      v-if="!header.compact"
                      class="fas sort-icon"
                      :class="{
                        'fa-sort': sortField !== header.field,
                        'fa-sort-up':
                          sortField === header.field && sortOrder === 'asc',
                        'fa-sort-down':
                          sortField === header.field && sortOrder === 'desc',
                      }"
                      title="Sort"
                    ></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(person, index) in filteredPeople"
                  :key="person.row_num"
                  @dblclick="handleDoubleClick($event, person)"
                  @click="selectRow($event, person, index)"
                  :class="{
                    'selected-row': selectedPeople
                      ? selectedPeople.some(
                          (selected) =>
                            selected.id === person.id &&
                            selected.Address === person.Address,
                        )
                      : '',
                    'odd-row': index % 2 === 1,
                    'even-row': index % 2 === 0,
                    'active-person': person.active === 1,
                  }"
                >
                  <td
                    v-for="(header, colIndex) in peopleTableHeaders"
                    :key="'td-' + colIndex"
                    :title="cellTitle(person, header)"
                    :style="{ textAlign: header.align || 'left' }"
                    :class="{
                      'overflow-ellipsis': header.overflow,
                      'col-compact': header.compact,
                      property: header.field === 'Address',
                      'sms-opt-cell': header.display === 'sms-opt',
                    }"
                    @dblclick="onPeopleCellDoubleClick($event, person, header)"
                  >
                    <!-- Notes: icon only (never show raw noteCount 0/1) -->
                    <div
                      v-if="header.display === 'note'"
                      class="click-target"
                      :title="'click to view\nor add notes'"
                      @click.stop="
                        selectedPeople[0] = person;
                        openNotesPanel(person.id);
                      "
                    >
                      <i
                        v-if="personHasNotes(person)"
                        class="fas fa-sticky-note"
                      ></i>
                    </div>

                    <!-- Staff-blacklisted ChatBot sessions -->
                    <span v-else-if="header.display === 'blacklist'">
                      <i
                        v-if="personHasBlacklistedTokens(person)"
                        class="fas fa-ban blacklist-token-icon"
                        title="Staff blacklisted PixieChatBot sessions"
                      ></i>
                    </span>

                    <!-- Valid unexpired ChatBot session (logged in) -->
                    <span v-else-if="header.display === 'chatbot-active'">
                      <i
                        v-if="personHasChatBotActiveSession(person)"
                        class="fas fa-mobile-alt chatbot-active-icon"
                        title="Active PixieChatBot session (valid unexpired token)"
                      ></i>
                    </span>

                    <!-- No active ChatBot session (logged out; not staff-blacklisted) -->
                    <span v-else-if="header.display === 'logged-out'">
                      <i
                        v-if="personHasChatBotLoggedOut(person)"
                        class="fas fa-sign-out-alt chatbot-logged-out-icon"
                        title="No active PixieChatBot session (logged out)"
                      ></i>
                    </span>

                    <!-- Boolean flags: checkmark only -->
                    <span v-else-if="header.display === 'check'">
                      <span
                        v-if="Number(person[header.field]) === 1"
                        style="color: green"
                        >✔</span
                      >
                    </span>

                    <span v-else-if="header.display === 'sms-opt'">
                      <span
                        v-if="peopleSmsOptInMark(person) === 'in'"
                        style="color: green"
                        >✔</span
                      >
                      <span
                        v-else-if="peopleSmsOptInMark(person) === 'out'"
                        style="color: #c0392b"
                        title="SMS opted out"
                        >✖</span
                      >
                    </span>

                    <span v-else-if="header.display === 'date'">
                      {{ formatDisplayDate(person[header.field]) }}
                    </span>

                    <template v-else>
                      {{ person[header.field] }}
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div
        v-if="smsOptPerson"
        class="small-dialog-overlay"
        @click.self="closeSmsOptPopup"
      >
        <div class="small-dialog-content sms-opt-dialog" @dblclick.stop>
          <div class="sms-opt-dialog-name">{{ smsOptPerson.fullName }}</div>
          <div>{{ smsOptStatusLabel(smsOptPerson) }}</div>
          <div
            v-if="smsOptPhoneLabel(smsOptPerson)"
            class="sms-opt-dialog-phone"
          >
            {{ smsOptPhoneLabel(smsOptPerson) }}
          </div>
          <div v-else class="sms-opt-dialog-phone">No phone on file</div>
          <p class="sms-opt-dialog-note">
            Opt in only if this person agreed to receive SMS from Big Property
            Management at this number about rent, work orders, and account
            notices. Reply STOP to opt out. Msg &amp; data rates may apply.
          </p>
          <div v-if="smsOptMessage" class="sms-opt-dialog-message">
            {{ smsOptMessage }}
          </div>
          <div class="small-dialog-buttons">
            <button
              type="button"
              :disabled="smsOptBusy || !smsOptCanOptIn(smsOptPerson)"
              @click="submitManualSmsOpt(true)"
            >
              Opt in
            </button>
            <button
              type="button"
              :disabled="smsOptBusy"
              @click="submitManualSmsOpt(false)"
            >
              Opt out
            </button>
            <button type="button" @click="closeSmsOptPopup">Cancel</button>
          </div>
        </div>
      </div>

      <!-- quick Dialog -->
      <div v-if="showDialogPopup" class="small-dialog-overlay">
        <div class="small-dialog-content">
          <div v-html="dialogMessage"></div>
          <div class="small-dialog-buttons">
            <button
              v-for="option in dialogOptions"
              :key="option"
              @click="onDialogOptionSelected(option)"
              :id="option === 'Confirm' ? 'confirmButton' : ''"
            >
              {{ option }}
            </button>
          </div>
        </div>
      </div>

      <!-- Copy Panel - Matching ManageProperties.vue structure -->
      <div
        v-if="showCopyPanel"
        ref="copyPanel"
        class="copy-panel"
        @dblclick="showCopyPanel = false"
      >
        <div class="copy-panel-header">
          Click name, email or phone to copy to clipboard
        </div>

        <!-- Person Name -->
        <div
          v-if="personToCopy"
          style="margin-top: 5px; cursor: pointer"
          @click="copyToClipboard(personToCopy)"
          class="copy-panel-item"
        >
          {{ personToCopy }}
        </div>

        <!-- Emails -->
        <div v-if="emailsToCopy.length > 0" style="margin-top: 5px">
          <label>Emails:</label>
          <div v-for="email in emailsToCopy" :key="email">
            <span
              class="copy-panel-item"
              @click="copyToClipboard(email)"
              style="cursor: pointer"
            >
              {{ email }}
            </span>
          </div>
        </div>

        <!-- Phones -->
        <div v-if="phonesToCopy.length > 0" style="margin-top: 5px">
          <label>Phones:</label>
          <div v-for="phone in phonesToCopy" :key="phone">
            <span
              class="copy-panel-item"
              @click="copyToClipboard(phone)"
              style="cursor: pointer"
            >
              {{ phone }}
            </span>
          </div>
        </div>

        <!-- Mini Calculator -->
        <div class="mini-calc">
          <small>quick calc: </small>
          <input
            v-model="calcExpression"
            @input="calculateResult"
            @keyup.enter="copyToClipboard(calcResult.toString())"
            placeholder="1+2"
            style="width: 50px; text-align: right"
          />
          =
          <span
            @click="copyToClipboard(calcResult.toString())"
            style="cursor: pointer; font-weight: bold"
          >
            {{ calcResult }}
          </span>
        </div>

        <!-- Copy Message -->
        <div v-if="copyMessage" class="copy-message">{{ copyMessage }}</div>
      </div>
    </div>
  </div>

  <NewPerson
    v-if="showTenantSearchDialog"
    :initialTenantQuery="this.selectedPerson"
    @closeDialog="handleDialogClosed"
  >
  </NewPerson>
  <!-- Importing NotesPanel -->
  <NotesPanel
    :showNotesPanel="this.showNotesPanel"
    :noteType="this.noteType"
    :noteId="this.noteId"
    @update-show-notes-panel="updateShowNotesPanel"
  />
  <div v-if="footerMessage" class="footer-message">{{ footerMessage }}</div>
</template>

<script>
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import NewPerson from './NewPerson.vue';
import NotesPanel from './NotesPanel.vue';
import { selectElementText } from '@/utils/selectElementText';
import {
  personMatchesPeopleSearch,
  peopleSmsOptInMark,
  placePeopleNotifColumn,
} from '@/utils/peopleListFilter';
import {
  peopleCellDoubleClickAction,
  manualSmsOptBody,
  manualSmsOptCanOptIn,
  smsOptPopupStatus,
  smsOptPopupPhone,
  patchPeopleSmsOpt,
} from '@/utils/peopleSmsManualOpt';

export default {
  components: { NewPerson, NotesPanel },
  created() {
    document.title = 'People Search';

    // Set search query immediately from route if present
    if (this.$route.query.search) {
      this.searchQuery = this.$route.query.search;
    }

    // Fetch properties and wait for completion
    this.fetchPeople()
      .then(() => {
        // Check for a saved property in local storage after properties are fetched
        const savedPerson = localStorage.getItem('selectedPeople');

        if (savedPerson) {
          try {
            this.selectedPeople = JSON.parse(savedPerson);

            const isValid = this.selectedPeople.every((savedPerson) =>
              this.peopleData.some((people) => people.id === savedPerson.id),
            );

            if (!isValid) {
              console.log('Selected people are not valid.');
              this.selectedPeople = [];
              localStorage.removeItem('selectedPeople');
            }
          } catch (error) {
            console.error(
              'Error parsing selectedPeople from local storage:',
              error,
            );
            // Clear the corrupted or undefined local storage entry
            this.selectedPeople = [];
            localStorage.removeItem('selectedPeople');
          }
        }
      })
      .catch((error) => {
        // Handle errors that occur during fetchPayments
        console.error('Failed to fetch people:', error);
      });
  },

  data() {
    return {
      startDate: null,
      showTenantSearchDialog: false,
      endDate: null,
      clickTimeout: null,
      searchQuery: '',
      // false = active tenants only (default); true = show everyone
      showAll: false,
      peopleData: [],
      lastSelectedIndex: null,
      selectedPeople: [],
      selectedPerson: null,

      peopleTableHeaders: placePeopleNotifColumn([
        { label: 'First', field: 'First', width: '100px', align: 'left' },
        { label: 'Last', field: 'Last', width: '100px', align: 'left' },
        {
          label: 'fullName',
          field: 'fullName',
          width: '100px',
          align: 'left',
          overflow: true,
        },
        {
          label: 'Email',
          field: 'Email',
          width: '150px',
          align: 'left',
          overflow: true,
        },
        {
          label: 'Phone',
          field: 'Phone',
          width: '100px',
          align: 'center',
          overflow: true,
        },
        {
          label: 'Address',
          field: 'Address',
          width: '150px',
          align: 'left',
          overflow: true,
        },
        {
          label: 'active',
          field: 'active',
          width: '40px',
          align: 'center',
          display: 'check',
        },
        {
          label: 'tenant',
          field: 'isTenant',
          width: '40px',
          align: 'center',
          display: 'check',
        },
        {
          label: 'contr.',
          field: 'isContractor',
          width: '40px',
          align: 'center',
          display: 'check',
        },
        {
          label: 'opt-in',
          field: 'smsOptIn',
          width: '52px',
          align: 'center',
          display: 'sms-opt',
          title: 'Double-click a cell to opt in or opt out',
        },
        {
          label: 'Since',
          field: 'activeFrom',
          width: '88px',
          align: 'center',
          display: 'date',
        },
        {
          label: 'N',
          field: 'noteCount',
          width: '32px',
          align: 'center',
          display: 'note',
          compact: true,
          title: 'Notes',
        },
        {
          label: 'BL',
          field: 'hasBlacklistedTokens',
          width: '32px',
          align: 'center',
          display: 'blacklist',
          compact: true,
          title: 'Staff blacklisted PixieChatBot sessions',
        },
        {
          label: 'In',
          field: 'hasChatBotActiveSession',
          width: '32px',
          align: 'center',
          display: 'chatbot-active',
          compact: true,
          title:
            'Active PixieChatBot session (valid unexpired token, typically 30 days)',
        },
        {
          label: 'Out',
          field: 'hasChatBotLoggedOut',
          width: '32px',
          align: 'center',
          display: 'logged-out',
          compact: true,
          title: 'No active PixieChatBot session (logged out or expired)',
        },
      ]),

      sortField: '',
      sortOrder: 'asc',
      showMergeButton: false,
      showDialogPopup: false,
      smsOptPerson: null,
      smsOptBusy: false,
      smsOptMessage: '',
      dialogMessage: '',
      dialogOptions: [],
      dialogCallback: null,
      showSearchPaymentsButton: false,
      showNotesPanel: false,
      noteType: 'tenant',
      noteId: null,
      footerMessage: '',

      // Copy panel properties
      showCopyPanel: false,
      copyMessage: '',
      calcResult: 0,
      personToCopy: '',
      emailsToCopy: [],
      phonesToCopy: [],
      calcExpression: '',
    };
  },

  mounted() {
    this.setDates(); // Set the startDate and endDate when the component mounts
    // this.fetchPeople(); // Fetch the payments using the startDate and endDate
    window.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('click', this.handleOutsideClick);
    if (this.$refs.searchQuery) this.$refs.searchQuery.focus();
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleOutsideClick);
  },

  computed: {
    // Compute the row count, prioritizing selectedPeople if any
    rowCount() {
      // Check if selectedPeople has any items
      if (this.selectedPeople && this.selectedPeople.length > 0) {
        // Create a Set of unique person IDs to ensure no duplicates
        const uniquePeople = new Set(
          this.selectedPeople.map((person) => person.id),
        );
        return uniquePeople.size;
      } else {
        // Otherwise return the length of filteredPeople
        return Array.isArray(this.filteredPeople)
          ? this.filteredPeople.length
          : 0;
      }
    },

    // Compute the sum of the amount field, prioritizing selectedPayments if any
    totalSum() {
      // Check if selectedPayments has any items
      if (this.selectedPeople) {
        const payments =
          this.selectedPeople.length > 0
            ? this.selectedPeople
            : this.filteredPeople;
        if (!Array.isArray(payments)) return '0.00';
        return payments
          .reduce((sum, doc) => {
            // Ensure that doc.amount is a valid number before adding it
            const amount = parseFloat(doc.amount) || 0;
            return sum + amount;
          }, 0)
          .toFixed(2);
      } else return 0.0;
    },

    // Filtered payments based on the search query and other conditions
    filteredPeople() {
      if (!Array.isArray(this.peopleData)) return [];

      // Split searchQuery into tokens by comma and trim whitespace
      const tokens = this.searchQuery
        .split(',')
        .map((token) => token.trim().toLowerCase());

      // Select initial data based on showAll variable
      let dataToProcess = this.showAll
        ? this.peopleData
        : this.peopleData.filter((person) => person.active === 1);

      // If the searchQuery is empty, return filtered data based on showAll
      if (tokens.length === 0 || tokens[0] === '') {
        return dataToProcess;
      }

      // Filter people that match all tokens in any field
      return dataToProcess.filter((person) =>
        personMatchesPeopleSearch(person, tokens),
      );
    },
  },

  methods: {
    peopleSmsOptInMark(person) {
      return peopleSmsOptInMark(person);
    },
    selectQuickCode(event) {
      selectElementText(
        event.currentTarget,
        () => window.getSelection(),
        () => document.createRange(),
      );
    },
    updateShowNotesPanel(newVal) {
      this.showNotesPanel = newVal;
      if (!newVal) {
        this.fetchPeople();
      }
    },

    openNotesPanel(id) {
      this.noteId = id;
      this.showNotesPanel = true;
    },

    setDates() {
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(today.getDate() - 30);

      this.endDate = today.toISOString().split('T')[0]; // Convert to YYYY-MM-DD
      this.startDate = endDate.toISOString().split('T')[0]; // Convert to YYYY-MM-DD
    },

    debounce(func, delay) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout); // Clear the previous timeout
        timeout = setTimeout(() => {
          func.apply(this, args); // Apply the function after the delay
        }, delay);
      }.bind(this);
    },

    personHasNotes(person) {
      return Number(person?.noteCount ?? 0) > 0;
    },

    personHasBlacklistedTokens(person) {
      return Number(person?.hasBlacklistedTokens ?? 0) === 1;
    },

    personHasChatBotLoggedOut(person) {
      return Number(person?.hasChatBotLoggedOut ?? 0) === 1;
    },

    personHasChatBotActiveSession(person) {
      return Number(person?.hasChatBotActiveSession ?? 0) === 1;
    },

    /**
     * Format a DB date as MM/DD/YYYY (e.g. 04/02/2026).
     * Prefer YYYY-MM-DD prefix so timezone shifts do not change the day.
     */
    formatDisplayDate(value) {
      if (value == null || value === '') return '';
      const s = String(value);
      const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) {
        return `${iso[2]}/${iso[3]}/${iso[1]}`;
      }
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    },

    cellTitle(person, header) {
      if (header.field === 'First') return `(${person.id})`;
      if (header.field === 'Email') return person.Email || '';
      if (header.field === 'Address') return 'double click to search address';
      if (
        header.field === 'hasBlacklistedTokens' &&
        this.personHasBlacklistedTokens(person)
      ) {
        return 'Staff blacklisted PixieChatBot sessions for this person';
      }
      if (
        header.field === 'hasChatBotActiveSession' &&
        this.personHasChatBotActiveSession(person)
      ) {
        return 'Active PixieChatBot session (valid unexpired token)';
      }
      if (
        header.field === 'hasChatBotLoggedOut' &&
        this.personHasChatBotLoggedOut(person)
      ) {
        return 'No active PixieChatBot session (logged out or expired)';
      }
      return header.title || '';
    },

    async fetchPeople() {
      await axios
        .get('/api/all-people', {})
        .then((response) => {
          // Assuming response.data contains the array of properties
          // example of tacking on a column
          this.peopleData = response.data.map((person, index) => ({
            ...person,
            // Normalize numeric flags from MySQL (may arrive as string/"0"/"1")
            smsOptIn: Number(person.smsOptIn) === 1 ? 1 : 0,
            noteCount: Number(person.noteCount ?? 0),
            hasBlacklistedTokens: Number(person.hasBlacklistedTokens ?? 0),
            hasChatBotActiveSession: Number(
              person.hasChatBotActiveSession ?? 0,
            ),
            hasChatBotLoggedOut: Number(person.hasChatBotLoggedOut ?? 0),
            // Preserve API default order for Esc reset
            _defaultOrder: index,
            extraProperty: 0,
          }));
          this.sortField = '';
          this.sortOrder = 'asc';
          // Return the data to indicate success
          return response.data;
        })
        .catch((error) => {
          console.error('Error fetching people:', error);
          // Handle error appropriately (e.g., show a message to the user)
          throw error; // Rethrow the error to propagate it to the caller
        });
    },

    formatDate(dateString) {
      const options = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        timeZone: 'UTC',
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    formatNumber(value, digits) {
      if (typeof value !== 'number') value = parseFloat(value);
      if (isNaN(value)) return '';
      if (value === 0.0) return '0.00';
      const sign = value < 0 ? '- ' : '';
      return `${sign}${Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}`;
    },

    clearSearch() {
      this.searchQuery = '';
    },

    singleClickHandler() {},

    getBalance() {
      // Iterate over each property in propertyData and append a balance of 0
      this.peopleData = this.peopleData.map((person) => ({
        ...person,
        balance: 0, // Placeholder value for balance
      }));
    },

    headerTitle(header) {
      const base = header.title || header.label || '';
      if (this.sortField === header.field) {
        const dir = this.sortOrder === 'asc' ? 'ascending' : 'descending';
        return `${base} (sorted ${dir} — Esc to reset)`;
      }
      return base ? `${base} (click to sort)` : 'Click to sort';
    },

    sortBy(field) {
      if (!field) return;
      // Flag/icon columns: first click shows matches first (desc)
      const preferDescFirst = [
        'noteCount',
        'hasBlacklistedTokens',
        'hasChatBotActiveSession',
        'hasChatBotLoggedOut',
      ].includes(field);

      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortOrder = preferDescFirst ? 'desc' : 'asc';
      }
      this.sortValues();
    },

    restoreDefaultSort() {
      this.sortField = '';
      this.sortOrder = 'asc';
      this.peopleData.sort(
        (a, b) => (a._defaultOrder ?? 0) - (b._defaultOrder ?? 0),
      );
    },

    sortValues() {
      if (!this.sortField) {
        this.restoreDefaultSort();
        return;
      }

      this.peopleData.sort((a, b) => {
        const sortMultiplier = this.sortOrder === 'asc' ? 1 : -1;
        const field = this.sortField;

        let aValue = a[field];
        let bValue = b[field];

        // Numeric flag columns (0/1, note counts)
        if (
          typeof aValue === 'number' ||
          typeof bValue === 'number' ||
          (aValue != null &&
            bValue != null &&
            !Number.isNaN(Number(aValue)) &&
            !Number.isNaN(Number(bValue)) &&
            String(aValue).trim() !== '' &&
            String(bValue).trim() !== '' &&
            /^-?\d+(\.\d+)?$/.test(String(aValue).trim()) &&
            /^-?\d+(\.\d+)?$/.test(String(bValue).trim()))
        ) {
          const an = Number(aValue ?? 0);
          const bn = Number(bValue ?? 0);
          if (!Number.isNaN(an) && !Number.isNaN(bn)) {
            if (an > bn) return 1 * sortMultiplier;
            if (an < bn) return -1 * sortMultiplier;
            // Stable tie-break by original API order
            return (a._defaultOrder ?? 0) - (b._defaultOrder ?? 0);
          }
        }

        // Sort ISO / display dates chronologically when possible
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const isoDate = /^(\d{4})-(\d{2})-(\d{2})/;
          const slashDate = /^(\d{2})\/(\d{2})\/(\d{2,4})$/;
          if (isoDate.test(aValue) && isoDate.test(bValue)) {
            aValue = aValue.slice(0, 10);
            bValue = bValue.slice(0, 10);
          } else if (slashDate.test(aValue) && slashDate.test(bValue)) {
            aValue = new Date(aValue);
            bValue = new Date(bValue);
          }
        }

        // Nulls last
        if (aValue == null || aValue === '') {
          if (bValue == null || bValue === '') {
            return (a._defaultOrder ?? 0) - (b._defaultOrder ?? 0);
          }
          return 1;
        }
        if (bValue == null || bValue === '') return -1;

        if (aValue > bValue) return 1 * sortMultiplier;
        if (aValue < bValue) return -1 * sortMultiplier;
        return (a._defaultOrder ?? 0) - (b._defaultOrder ?? 0);
      });
    },

    selectRow(event, person, index) {
      // Check if the shift key is pressed (shift-click functionality)
      if (event.shiftKey && this.lastSelectedIndex !== null) {
        const start = Math.min(this.lastSelectedIndex, index);
        const end = Math.max(this.lastSelectedIndex, index);

        for (let i = start; i <= end; i++) {
          if (!this.selectedPeople.includes(this.filteredPeople[i])) {
            this.selectedPeople.push(this.filteredPeople[i]);
          }
        }
      }
      // Check if the ctrl key (or cmd key on Mac) is pressed (ctrl-click functionality)
      else if (event.ctrlKey || event.metaKey) {
        const personIndex = this.selectedPeople.indexOf(person);

        if (personIndex === -1) {
          this.selectedPeople.push(person); // Add to selection
        } else {
          this.selectedPeople.splice(personIndex, 1); // Remove from selection
        }
      }
      // Regular click, no special keys
      else {
        this.selectedPeople = [person]; // Replace with the clicked row
      }

      // Store the last selected index for shift-click behavior
      this.lastSelectedIndex = index;

      if (this.selectedPeople.length === 1) {
        // Check if there is exactly one row selected
        this.showSearchPaymentsButton = true;
      }

      // Check if there are exactly two selected people and if they have matching emails
      else if (this.selectedPeople.length === 2) {
        const [person1, person2] = this.selectedPeople;

        if (person1.Email && person2.Email) {
          // Split the emails for each person by semicolon, trim spaces, and store them in arrays
          const person1Emails = person1.Email.toLowerCase()
            .split(';')
            .map((email) => email.trim());
          const person2Emails = person2.Email.toLowerCase()
            .split(';')
            .map((email) => email.trim());

          // Check if at least one email from person1 matches any email from person2
          const hasMatchingEmail = person1Emails.some((email1) =>
            person2Emails.includes(email1),
          );

          // Show the merge button if there's a matching email
          if (hasMatchingEmail) {
            this.showMergeButton = true;
          } else {
            this.showMergeButton = false;
          }
        } else {
          this.showMergeButton = false; // If either person has no emails, don't show the merge button
        }
      } else {
        this.showMergeButton = false; // If not exactly 2 people are selected, hide the merge button
      }

      // Update local storage or any other related behavior
      localStorage.setItem(
        'selectedPeople',
        JSON.stringify(this.selectedPeople),
      );
    },

    onDateChange() {
      // Check if both startDate and endDate are valid dates
      const startDate = new Date(this.startDate);
      const endDate = new Date(this.endDate);

      if (
        !this.startDate ||
        !this.endDate ||
        isNaN(startDate.getTime()) ||
        isNaN(endDate.getTime())
      ) {
        return; // Exit the function if either date is invalid
      }

      // Check if startDate is earlier than endDate
      if (startDate >= endDate) {
        return; // Exit the function if startDate is not earlier than endDate
      }

      // If validation passes, fetch payments
      this.fetchPeople();
    },

    handleKeyDown(event) {
      // If the Escape key is pressed
      if (event.key === 'Escape') {
        if (this.smsOptPerson) {
          this.closeSmsOptPopup();
          return;
        }
        if (this.showCopyPanel) {
          this.showCopyPanel = false;
          return;
        }
        // Clear custom column sort → restore API default order
        if (this.sortField) {
          this.restoreDefaultSort();
          return;
        }
        if (this.selectedPeople.length === 0) {
          this.searchQuery = '';
          if (this.$refs.searchQuery) this.$refs.searchQuery.focus();
        } else {
          this.selectedPeople = [];
          localStorage.removeItem('selectedPeople');
        }
      }
    },

    onPeopleCellDoubleClick(event, person, header) {
      if (peopleCellDoubleClickAction(header) !== 'sms-opt') return;
      event.stopPropagation();
      this.openSmsOptPopup(person);
    },

    openSmsOptPopup(person) {
      this.smsOptPerson = person;
      this.smsOptMessage = '';
      this.smsOptBusy = false;
    },

    closeSmsOptPopup() {
      this.smsOptPerson = null;
      this.smsOptMessage = '';
    },

    smsOptStatusLabel(person) {
      return smsOptPopupStatus(person);
    },

    smsOptPhoneLabel(person) {
      return smsOptPopupPhone(person);
    },

    smsOptCanOptIn(person) {
      return manualSmsOptCanOptIn(person);
    },

    async submitManualSmsOpt(optIn) {
      const person = this.smsOptPerson;
      if (!person || this.smsOptBusy) return;
      if (optIn && !manualSmsOptCanOptIn(person)) {
        this.smsOptMessage = 'Set a 10-digit phone first.';
        return;
      }
      this.smsOptBusy = true;
      this.smsOptMessage = '';
      try {
        const response = await axios.post(
          `/api/people/${person.id}/sms`,
          manualSmsOptBody(person, optIn),
        );
        const saved = response.data || {};
        this.peopleData = patchPeopleSmsOpt(this.peopleData, person.id, saved);
        this.selectedPeople = patchPeopleSmsOpt(
          this.selectedPeople,
          person.id,
          saved,
        );
        this.footerMessage = optIn ? 'SMS opt-in saved.' : 'SMS opt-out saved.';
        this.smsOptPerson = null;
      } catch (error) {
        this.smsOptMessage =
          error.response?.data?.error || 'Failed to update SMS opt-in';
      } finally {
        this.smsOptBusy = false;
      }
    },

    handleDoubleClick(event, person) {
      // Check for CTRL+double-click to show copy panel - this takes precedence
      if (event.ctrlKey) {
        this.showCopyPanelForPerson(event, person);
        return; // Exit early to prevent other double-click actions
      }

      const clickedElement = event.target;
      this.selectedPerson = person.fullName;

      // Check if the clicked element is within the name
      if (clickedElement.classList.contains('fullName')) {
        this.searchQuery = person.fullName;
        this.fetchPeople();
        return; // Exit early to prevent showing tenant search dialog
      }

      // Check if the clicked element is within the property address
      if (clickedElement.classList.contains('property')) {
        if (person.Address) {
          // const strippedAddress = person.Address.replace(/\s*\(.*?\)\s*/g, ''); // Remove content within parentheses and surrounding spaces
          const lease = ':' + person.leaseId;
          this.navigateToLeaseSearch(lease);
        }
        return; // Exit early to prevent showing tenant search dialog
      }

      // Default action: show tenant search dialog
      this.showTenantSearchDialog = true;
    },

    handleOutsideClick(event) {
      if (
        this.showCopyPanel &&
        this.$refs.copyPanel &&
        !this.$refs.copyPanel.contains(event.target)
      ) {
        this.showCopyPanel = false;
      }
    },

    showCopyPanelForPerson(event, person) {
      // Populate copy data
      this.personToCopy = person.fullName || '';

      // Handle emails - split by semicolon if multiple
      this.emailsToCopy = person.Email
        ? person.Email.split(';').map((email) => email.trim())
        : [];

      // Handle phones - split by semicolon if multiple
      this.phonesToCopy = person.Phone
        ? person.Phone.split(';').map((phone) => phone.trim())
        : [];

      // Position the copy panel at the click location (matching ManageProperties.vue exactly)
      this.$nextTick(() => {
        console.log('Event coordinates:', event.clientX, event.clientY);

        if (this.$refs.copyPanel) {
          console.log('copyPanel:', this.$refs.copyPanel);
          this.$refs.copyPanel.style.position = 'absolute';
          this.$refs.copyPanel.style.left = `${event.clientX}px`;

          // Adjust top position if too close to the bottom of the screen
          const panelHeight = this.$refs.copyPanel.offsetHeight;
          const viewportHeight = window.innerHeight;
          let topPosition = event.clientY;

          if (topPosition + panelHeight > viewportHeight) {
            topPosition = viewportHeight - panelHeight - 100; // Adjust to ensure full visibility
          }

          this.$refs.copyPanel.style.top = `${topPosition}px`;
        }
      });

      this.showCopyPanel = true;
    },

    copyToClipboard(text) {
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            this.copyMessage = 'Copied!';
            setTimeout(() => {
              this.copyMessage = '';
            }, 2000);
          })
          .catch((err) => {
            console.error('Failed to copy text: ', err);
          });
      } else {
        this.copyMessage = 'not copied! (only works in production)';
        setTimeout(() => {
          this.copyMessage = '';
        }, 2000);
      }
    },

    calculateResult() {
      try {
        // Basic calculator - evaluate simple expressions
        // Remove any non-numeric characters except +, -, *, /, ., (, )
        const sanitized = this.calcExpression.replace(/[^0-9+\-*/.() ]/g, '');
        if (sanitized) {
          this.calcResult = Function(
            '"use strict"; return (' + sanitized + ')',
          )();
        }
      } catch (error) {
        this.calcResult = 'Error';
      }
    },

    navigateToLeaseSearch(address) {
      // Navigate to LeaseSearch page with query parameter
      // this.$router.push({ path: '/lease-search', query: { search: searchValue.address } });
      // Open LeaseSearch page in a new tab with the query parameter
      const url = this.$router.resolve({
        path: '/lease-search',
        query: { search: address },
      }).href;

      window.open(url, '_blank'); // Open the URL in a new tab
    },
    handleDialogClosed() {
      this.showTenantSearchDialog = false;
      this.fetchPeople();
    },
    mergePeople() {
      // Check if there are exactly two selected people
      if (this.selectedPeople.length !== 2) {
        return; // If not, exit early
      }

      // one of the two selected people must be 'Unknown' in person.First
      if (
        !this.selectedPeople.some(
          (person) => person.First === 'Unknown' || person.Last === 'Unknown',
        )
      ) {
        this.showDialog(
          'One person must have Unknown in First and Last name fields.',
          ['OK'],
        ).then((option) => {
          if (option === 'OK') {
            return;
          }
        });
      } else {
        // Get the two selected IDs based on their names (known or unknown)
        const fromId = this.selectedPeople.find(
          (person) => person.First === 'Unknown',
        ).id;
        const toId = this.selectedPeople.find(
          (person) => person.First !== 'Unknown',
        ).id;
        const knownPerson = this.selectedPeople.find(
          (person) => person.First !== 'Unknown',
        ); // Get the full object for the known person

        // Check if the known person has an assigned leaseId
        if (!knownPerson.leaseId) {
          this.showDialog(
            'The known person in the merge must have an assigned lease first.',
            ['OK'],
          );
          return; // Stop further processing if there is no leaseId
        }

        // Show confirmation dialog before proceeding
        this.showDialog(
          'Do you want to merge these two rows and move payments from the "Unknown" person to the known person?',
          ['Cancel', 'Confirm'],
        ).then((option) => {
          if (option === 'Confirm') {
            // Proceed with the merge if confirmed
            axios
              .post('/api/merge-people', { fromId, toId })
              .then(() => {
                this.fetchPeople(); // Refresh the people data after the merge
                this.selectedPeople = []; // Clear the selected people
                localStorage.setItem(
                  'selectedPeople',
                  JSON.stringify(this.selectedPeople),
                ); // Update local storage
              })
              .catch((error) => {
                console.error('Error merging people:', error);
              });
          } else {
            console.log('Merge cancelled');
          }
        });
      }
    },

    showDialog(message, options) {
      this.dialogMessage = message;
      this.dialogOptions = options;
      this.showDialogPopup = true;

      return new Promise((resolve) => {
        this.dialogCallback = resolve;
      });
    },

    onDialogOptionSelected(option) {
      this.showDialogPopup = false;
      if (this.dialogCallback) {
        this.dialogCallback(option);
        this.dialogCallback = null; // Clear the callback
      }
    },
    searchPayments() {
      const fullName = this.selectedPeople[0].fullName;

      // Navigate to the /payments route with the fullName as a search query
      this.$router.push({
        path: '/payments',
        query: { search: fullName },
      });
    },
  },
  watch: {},
};
</script>

<style scoped>
.people-page {
  margin-left: 25px;
  margin-right: 25px;
  padding: 0px;
  width: min(100% - 50px, 1380px);
  max-width: 1380px;
  height: 95%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.page-title {
  font-size: 14pt;
  font-weight: bold;
}
.search-bar {
  margin-top: 15px;
  font-weight: bold;
  font-size: 0.9em;
  margin-bottom: 5px;
}

.search-bar input {
  height: 25px;
  width: 250px;
  border-radius: 4px;
  border-color: #ccc;
}
.search-bar button {
  height: 30px;
  border-radius: 3px;
  padding: 4px;
  border-color: #ccc;
  background-color: #ccc;
  margin-left: 4px;
}
.search-bar .label {
  margin-bottom: 13px;
}
.table-container {
  position: relative;
  width: 100%;
  user-select: none;
  margin-top: 12px;
  flex: 1;
  min-height: 0;
}

.table-scroll {
  max-height: min(620px, calc(100vh - 220px));
  overflow: auto;
  border: 1pt solid #bdbcbc;
  border-radius: 4px;
  background: #fff;
}

.people-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 9pt;
}

.people-table th,
.people-table td {
  padding: 5px 6px;
  border-bottom: 0.1em solid #e4e4e4;
  border-left: 1pt solid #ccc;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Narrow icon/letter columns: no ellipsis, full glyph visible */
.people-table th.col-compact,
.people-table td.col-compact {
  overflow: hidden;
  text-overflow: clip;
  padding-left: 2px;
  padding-right: 2px;
  text-align: center;
}

.people-table th:first-child,
.people-table td:first-child {
  border-left: none;
}

.people-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #888;
  color: #f5f5f5;
  font-size: 1.05em;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 0 #777;
}

/* Active sort column (no arrows on compact cols — color only) */
.people-table thead th.sorted-col {
  background: #6b5b95;
  color: #fff;
  box-shadow: inset 0 -3px 0 #f0c14b;
}

.people-table thead th.sorted-col.sorted-desc {
  background: #5a4a7a;
}

.people-table thead th.sorted-col.sorted-asc {
  background: #7d6ba8;
}

.people-table thead th.col-compact {
  font-size: 1em;
  letter-spacing: 0;
}

.people-table thead th.col-compact .th-label {
  margin-right: 0;
  display: inline-block;
  width: 100%;
  text-align: center;
}

.people-table thead th .th-label {
  margin-right: 4px;
}

.people-table thead th .sort-icon {
  font-size: 0.85em;
  opacity: 0.85;
  padding-left: 2px;
}

.people-table tbody tr {
  cursor: pointer;
}

.selected-row {
  background-color: #baa1bd !important; /* Highlight selected row */
  /* color: #eee; */
}

.odd-row {
  background-color: #ddd; /* Example alternating row color */
}

.even-row {
  background-color: #ffffff;
}
.is-expired {
  color: #5f53c1a1;
  font-size: 1.1em;
}

.search-input {
  border-radius: 4px;
}

label {
  margin-left: 10px;
}

.date-picker {
  width: 100px !important;
}

.count {
  font-weight: normal;
}

.sum {
  font-weight: normal;
}

.overflow-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* small dialog */
.small-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.small-dialog-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 14px 8px 7px 2px rgba(0, 0, 0, 0.1);
  z-index: 2000;
  width: 400px;
}

.small-dialog-buttons button {
  margin: 10px;
  padding: 10px 20px;
  background-color: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.small-dialog-buttons button:hover {
  background-color: #a080a8;
}

.sms-opt-cell {
  cursor: pointer;
}

.sms-opt-dialog {
  width: 460px;
}

.sms-opt-dialog-name {
  font-weight: bold;
  margin-bottom: 6px;
}

.sms-opt-dialog-phone {
  margin-top: 4px;
}

.sms-opt-dialog-note {
  margin: 12px 0 0;
  font-size: 0.9em;
  line-height: 1.35;
}

.sms-opt-dialog-message {
  margin-top: 10px;
}

.small-dialog-buttons button:disabled {
  opacity: 0.55;
  cursor: default;
}

.footer-message {
  position: fixed;
  bottom: 8px;
  left: 16px;
  z-index: 1002;
  background: #9b7ba8;
  color: white;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 0.85em;
}

.hint {
  font-weight: normal;
  font-size: 0.9em;
  font-style: italic;
}

.property {
  text-decoration: underline;
  text-decoration-color: #aaa;
  text-decoration-thickness: 1pt;
}

.click-target {
  width: 32px;
  height: 20px;
  margin: -5px;
}

.active-person {
  font-weight: bold;
}

.blacklist-token-icon {
  color: #c0392b;
  font-size: 0.95em;
}

.chatbot-active-icon {
  color: #1e8449;
  font-size: 0.95em;
}

.chatbot-logged-out-icon {
  color: #6c757d;
  font-size: 0.95em;
}

.content-wrapper {
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.1);
  background: #ddd;
  padding: 16px 18px 20px;
  border-radius: 5px;
  margin-top: 8px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 640px;
  max-height: calc(100vh - 60px);
}

.active-class {
  background: #a080a8 !important;
}

.active-note {
  font-size: 0.8em;
  font-style: italic;
  margin-top: 8px;
  margin-left: 3px;
}

.quick-code-display {
  font-family: 'Courier New', Courier, monospace;
  font-size: 1.8rem;
  position: absolute;
  right: 70px;
  color: #aeadad;
  cursor: text;
  -webkit-user-select: all;
  user-select: all;
}

.clear-button {
  margin-left: -4px;
}

/* Copy Panel Styles - Matching ManageProperties.vue exactly */
.copy-panel {
  width: 284px;
  z-index: 9999;
  background: #fff;
  position: absolute;
  top: 40px;
  left: 40px;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.25);
  font-size: 0.8em;
  border: 1px solid #888;
  padding: 6px;
  border-radius: 5px;
  cursor: pointer;
}

.copy-panel label {
  margin-top: 5px;
  font-weight: bold;
}

.copy-panel-header {
  margin: -6px;
  height: 28px;
  background: #333;
  color: #fff;
  padding: 5px;
  text-align: center;
  margin-bottom: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  font-size: 1.1em;
}

.copy-message {
  background: green;
  color: #fff;
  font-size: 9px;
  margin-top: 5px;
  margin-bottom: 5px;
  transition: opacity 0.5s ease-out;
  padding: 4px;
  min-height: 10px;
  height: 10px;
}

.copy-panel-item:hover {
  background: #dfbee4;
}

.mini-calc {
  margin-top: 10px;
}

.mini-calc input {
  border-radius: 5px;
  border: 1pt solid #ddd;
  outline: none;
  font-size: 0.9em;
  font-size: 12px;
}

.calc-result {
  font-weight: bold;
  min-width: 60px;
  text-align: right;
}

.copy-message {
  margin-top: 10px;
  padding: 6px 8px;
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  font-size: 11px;
  text-align: center;
}
</style>
